import React, { useEffect, useState , useMemo } from 'react'
import { useCurrent, useFetch } from '../hooks';
import { Link, useParams } from 'react-router-dom';
import MonthlyLineChart from '../components/dashborad/MonthlyLineChart';
import { FaPhoneAlt, FaEnvelope, FaPlus, FaTicketAlt } from "react-icons/fa";
import { useTranslation } from 'react-i18next';
import { getStatusAppearance, getStatusId, normalizeSupportSettings } from '../constants/ticket';
import MwSpinnerButton from '../components/ui/MwSpinnerButton';

/* ─────────────────────────────────────────
   Total Ticket Card
───────────────────────────────────────── */
const TotalCard = ({ label, subLabel, loading, data, trend, trendUp = true, icon, iconBg, iconColor, barColor, to }) => {
    const content = (
        <div className="bg-white border border-slate-100 hover:border-slate-200 transition-colors rounded-[18px] p-5 flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <div className="w-[38px] h-[38px] rounded-[11px] flex items-center justify-center flex-shrink-0" style={{ background: iconBg, color: iconColor }}>
                    {icon}
                </div>
                {trend !== undefined && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full" style={{ background: iconBg, color: iconColor }}>
                        {trendUp
                            ? <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 7l3-4 3 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            : <svg width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 3l3 4 3-4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
                        }
                        {trend}
                    </span>
                )}
            </div>
            <div>
                <div className="text-[30px] font-bold leading-none text-[#251658]">
                    {loading ? <div className="h-8 flex items-center"><MwSpinnerButton withLabel={false} /></div> : data}
                </div>
                <div className="text-xs text-[#4a3c78] font-semibold mt-1.5">{label}</div>
            </div>
            <div className="flex flex-col gap-1.5">
                <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-1000" style={{ width: loading ? '0%' : '100%', background: barColor }} />
                </div>
                <div className="flex justify-between text-[11px] text-[#6b5f91] font-medium">
                    <span>{subLabel}</span>
                    <span style={{ color: barColor, fontWeight: 500 }}>100%</span>
                </div>
            </div>
        </div>
    );
    return to ? <Link to={to} className="block">{content}</Link> : content;
};

/* ─────────────────────────────────────────
   Status Strip Row — single line, bold & modern
───────────────────────────────────────── */
const StatusStrip = ({ statuses, statusCounts, allTicketCount, loading, isAr, tenantUsername }) => {
    if (loading) {
        return (
            <div className="bg-white border border-slate-100 rounded-[16px] px-5 py-3.5 flex items-center gap-3">
                <MwSpinnerButton withLabel={false} />
            </div>
        );
    }

    return (
        <div className="bg-white border border-slate-100 rounded-[16px] px-4 py-3 w-full min-w-0 max-w-full overflow-x-auto">
            <div className="flex items-center flex-nowrap whitespace-nowrap w-full min-w-max justify-between">
                <Link
                    to={`/${tenantUsername}/tickets`}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-bold transition-all hover:scale-[1.04] active:scale-[0.97] select-none flex-shrink-0"
                    style={{
                        background: '#f1f5f9',
                        color: '#334155',
                        border: '1.5px solid #64748b22',
                    }}
                >
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: '#64748b' }} />
                    <span>{isAr ? 'كل الحالات' : 'All statuses'}</span>
                    <span
                        className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-black"
                        style={{ background: '#64748b22', color: '#334155' }}
                    >
                        {Number(allTicketCount || 0)}
                    </span>
                </Link>
                {statuses.map((status) => {
                    const id = String(status?._id || '');
                    const count = Number(statusCounts?.[id] || 0);
                    const pct = allTicketCount > 0 ? Math.round((count / allTicketCount) * 100) : 0;
                    const label = isAr ? status.labelAr : status.labelEn;
                    const appearance = status._appearance || { color: '#534AB7', bg: '#EEEDFE', dot: '#534AB7' };

                    return (
                        <Link
                            key={id}
                            to={`/${tenantUsername}/tickets?status=${encodeURIComponent(id)}`}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-bold transition-all hover:scale-[1.04] active:scale-[0.97] select-none flex-shrink-0"
                            style={{
                                background: appearance.bg,
                                color: appearance.color,
                                border: `1.5px solid ${appearance.dot}22`,
                            }}
                        >
                            <span
                                className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                style={{ background: appearance.dot }}
                            />
                            <span>{label}</span>
                            <span
                                className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-black"
                                style={{ background: appearance.dot + '22', color: appearance.color }}
                            >
                                {count}
                            </span>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
};

/* ─────────────────────────────────────────
   Dashboard Page
───────────────────────────────────────── */
const DashBoard = () => {
    const { currentTenantId, currentCompanyId, currentBranchId, currentLangId, user } = useCurrent();
    const [t] = useTranslation('global');
    const tenantUsername = useParams()?.tenant;
    const isAr = currentLangId === 'ar';

    const [allTicketCount, setAllTicketCount]               = useState(0);
    const [monthlyList, setMonthlyList]                     = useState([]);
    const [statusCounts, setStatusCounts] = useState({});
    const [thisMonthTicketCount, setThisMonthTicketCount] = useState(0);
    const [thisYearTicketCount, setThisYearTicketCount] = useState(0);
    const [lastYearTicketCount, setLastYearTicketCount] = useState(0);

    const tenantId = currentTenantId || user?.tenant_id;
    const companyId = currentCompanyId || user?.company_id;
    const branchId = currentBranchId || user?.branch_id;
    const ticketsUrl =
        tenantId && companyId && branchId
            ? `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/tenant/${tenantId}/${companyId}/${branchId}`
            : null;

    const { data: resultTickets, loading: loadingTickets } = useFetch(ticketsUrl, Boolean(ticketsUrl));
    const { data: supportSettingsResult } = useFetch(
        `${process.env.REACT_APP_SUPPORT_API_BASE_URL}/settings`
    );
    const supportSettings = useMemo(
        () => normalizeSupportSettings(supportSettingsResult),
        [supportSettingsResult]
    );
    const activeStatuses = useMemo(
        () => (supportSettings?.statuses || []).filter((item) => item?.isActive !== false).sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0)),
        [supportSettings?.statuses]
    );

    // enrich statuses with appearance for StatusStrip
    const enrichedStatuses = useMemo(
        () => activeStatuses.map(status => {
            const id = String(status?._id || '');
            return { ...status, _appearance: getStatusAppearance(supportSettings?.statuses, id) };
        }),
        [activeStatuses, supportSettings?.statuses]
    );

    useEffect(() => {
        if (!resultTickets) return;
        const tickets = resultTickets?.data || [];
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth();
        let nextMonthCount = 0;
        let nextYearCount = 0;
        let nextLastYearCount = 0;
        tickets.forEach((ticket) => {
            const createdAt = ticket?.createdAt ? new Date(ticket.createdAt) : null;
            if (!createdAt || Number.isNaN(createdAt.getTime())) return;
            const y = createdAt.getFullYear();
            if (y === currentYear) {
                nextYearCount += 1;
                if (createdAt.getMonth() === currentMonth) nextMonthCount += 1;
            } else if (y === currentYear - 1) {
                nextLastYearCount += 1;
            }
        });
        setAllTicketCount(tickets.length);
        setThisMonthTicketCount(nextMonthCount);
        setThisYearTicketCount(nextYearCount);
        setLastYearTicketCount(nextLastYearCount);
        setMonthlyList(
            Array.from({ length: 12 }, (_, i) =>
                tickets.filter(r => new Date(r.createdAt).getMonth() + 1 === i + 1).length
            )
        );
        const nextCounts = {};
        activeStatuses.forEach((status) => {
            nextCounts[String(status?._id || '')] = 0;
        });
        tickets.forEach((ticket) => {
            const resolved = getStatusId(supportSettings?.statuses, ticket.statusId || ticket.status);
            if (resolved && Object.prototype.hasOwnProperty.call(nextCounts, String(resolved))) {
                nextCounts[String(resolved)] += 1;
            }
        });
        setStatusCounts(nextCounts);
    }, [activeStatuses, resultTickets, supportSettings?.statuses]);

    return (
        <div className="flex flex-col gap-6 pb-10">

            {/* ══════════════════════════════════════════
                HERO HEADER — أبيض نظيف بدون لون داكن
            ══════════════════════════════════════════ */}
            <div className="bg-white border border-slate-100 rounded-[20px] relative overflow-hidden">

                {/* accent line at top */}
                <div className="absolute top-0 left-9 right-9 h-[2px] bg-[#534AB7] rounded-b-sm" />

                {/* subtle dot-grid texture at bottom */}
                <div
                    className="absolute bottom-0 left-0 right-0 h-28 pointer-events-none"
                    style={{
                        opacity: 0.03,
                        backgroundImage: 'radial-gradient(circle, #000 1px, transparent 1px)',
                        backgroundSize: '20px 20px',
                    }}
                />

                <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6 p-8 md:p-10 flex-wrap">

                    {/* ── Left: branding + copy ── */}
                    <div className="flex flex-col gap-3.5 max-w-md text-center md:text-start">

                        {/* eyebrow pill */}
                        <div className="inline-flex items-center gap-2 self-center md:self-start bg-[#EEEDFE] rounded-full px-3.5 py-1.5 w-fit">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#534AB7] flex-shrink-0" />
                            <span className="text-[11px] text-[#534AB7] font-semibold">
                                {isAr ? 'نظام الدعم الفني' : 'Support System'}
                            </span>
                        </div>

                        <h1 className="text-[26px] md:text-[28px] font-bold leading-snug text-[#251658]">
                            {t('support.dashboard.title')}
                        </h1>

                        <p className="text-sm text-[#6b5f91] font-medium leading-relaxed">
                            {t('support.dashboard.welcome1')} {t('support.dashboard.welcome2')}
                        </p>
                    </div>

                    {/* ── Right: CTA + contacts ── */}
                    <div className="flex flex-col gap-3 w-full md:w-auto md:min-w-[240px]">

                        <Link to={`/${tenantUsername}/tickets/add`} className="w-full">
                            <button className="w-full bg-[#534AB7] hover:bg-[#3C3489] active:scale-[0.98] transition-all text-white px-6 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2">
                                <FaPlus size={11} />
                                {t('support.dashboard.newTicket')}
                            </button>
                        </Link>

                        {/* contact card */}
                        <div className="border border-slate-100 rounded-xl overflow-hidden">
                            <div className="flex items-center gap-2.5 px-3.5 py-2.5">
                                <div className="w-7 h-7 rounded-lg bg-[#EEEDFE] flex items-center justify-center flex-shrink-0">
                                    <FaPhoneAlt size={11} className="text-[#534AB7]" />
                                </div>
                                <span className="text-xs text-[#4a3c78] font-semibold" dir="ltr">
                                    {supportSettings.contact.phone}
                                </span>
                            </div>
                            <div className="h-px bg-slate-100" />
                            <div className="flex items-center gap-2.5 px-3.5 py-2.5">
                                <div className="w-7 h-7 rounded-lg bg-[#EEEDFE] flex items-center justify-center flex-shrink-0">
                                    <FaEnvelope size={11} className="text-[#534AB7]" />
                                </div>
                                <span className="text-xs text-[#4a3c78] font-semibold">
                                    {supportSettings.contact.email}
                                </span>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            {/* ══════════════════════════════════════════
                ACTIVITY OVERVIEW
            ══════════════════════════════════════════ */}
            <div className="flex flex-col gap-5">

                {/* section label */}
                <div className="flex items-center gap-2.5">
                    <span className="w-0.5 h-4 rounded-sm bg-[#534AB7]" />
                    <h2 className="text-[13px] font-semibold text-[#6b5f91]">
                        {isAr ? 'نظرة عامة على النشاط' : 'Activity Overview'}
                    </h2>
                </div>

                {/* ── Total ticket card ── */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <TotalCard
                        label={isAr ? 'تذاكر الشهر الحالي' : 'Tickets this month'}
                        subLabel={isAr ? 'الشهر الحالي' : 'This month'}
                        loading={loadingTickets}
                        data={thisMonthTicketCount}
                        icon={<FaTicketAlt size={17} />}
                        iconBg="#EEEDFE"
                        iconColor="#534AB7"
                        barColor="#534AB7"
                        to={`/${tenantUsername}/tickets`}
                    />
                    <TotalCard
                        label={isAr ? 'تذاكر السنة الحالية' : 'Tickets this year'}
                        subLabel={isAr ? 'السنة الحالية' : 'This year'}
                        loading={loadingTickets}
                        data={thisYearTicketCount}
                        icon={<FaTicketAlt size={17} />}
                        iconBg="#EAF7FF"
                        iconColor="#0284c7"
                        barColor="#0284c7"
                        to={`/${tenantUsername}/tickets`}
                    />
                    <TotalCard
                        label={isAr ? 'تذاكر العام الماضي' : 'Tickets last year'}
                        subLabel={isAr ? 'العام الماضي' : 'Last year'}
                        loading={loadingTickets}
                        data={lastYearTicketCount}
                        icon={<FaTicketAlt size={17} />}
                        iconBg="#FCE7F3"
                        iconColor="#be185d"
                        barColor="#be185d"
                        to={`/${tenantUsername}/tickets`}
                    />
                </div>

                {/* ── Status strip row ── */}
                <StatusStrip
                    statuses={enrichedStatuses}
                    statusCounts={statusCounts}
                    allTicketCount={allTicketCount}
                    loading={loadingTickets}
                    isAr={isAr}
                    tenantUsername={tenantUsername}
                />

                {/* ── Yearly chart card ── */}
                <div className="bg-white border border-slate-100 rounded-[18px] p-6 md:p-8">

                    <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                        <div>
                            <h2 className="text-[15px] font-bold text-[#251658]">
                                {t('support.dashboard.yearlyTickets')}
                            </h2>
                            <p className="text-xs text-[#6b5f91] font-medium mt-1">
                                {isAr
                                    ? 'إحصائيات تذاكر الدعم خلال الـ 12 شهراً الماضية'
                                    : 'Support tickets over the last 12 months'}
                            </p>
                        </div>

                        {!loadingTickets && monthlyList.some(v => v > 0) && (
                            <div className="flex items-center gap-2 bg-[#EEEDFE] rounded-full px-3.5 py-1.5">
                                <span className="w-2 h-2 rounded-full bg-[#534AB7]" />
                                <span className="text-[11px] text-[#534AB7] font-semibold">
                                    {isAr ? 'عدد التذاكر' : 'Tickets'}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="h-[280px] md:h-[360px]">
                        <MonthlyLineChart
                            tenantUsername={tenantUsername}
                            loading={loadingTickets}
                            monthlyList={monthlyList}
                            label={t('support.dashboard.yearlyTickets')}
                        />
                    </div>

                </div>

            </div>
        </div>
    );
};

export default DashBoard;
